import { useCallback, useEffect, useMemo } from 'react'
import i18n from 'modules/core/lib/i18n'

import { SyncReviewModal } from 'modules/integrations/components/SyncButton/Modals/SyncReviewModal'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useSyncValidation } from 'modules/integrations/hooks/SyncContext/useSyncValidation'
import { useIssuesWithModalClose } from 'modules/integrations/hooks/SyncContext/issues'

const EXCLUDED_DATA_COLLECTION_KEYS = [
  'payrollRunId',
  'marketplaceIntegrationId',
  'includeSyncableState',
  'companyId',
  'sortOrder',
  'companyIdString',
  'includeGrouped',
  'includeManualDrafts',
  'from',
  'to',
]

export interface ModalsProps {
  setShowModal: (showModal: boolean) => void
  onConfirm?: () => void
  skipConfirmationIfNoIssues?: boolean
}

export const Modals = ({
  setShowModal,
  onConfirm,
  skipConfirmationIfNoIssues = false,
}: ModalsProps) => {
  const {
    runSynchronisation,
    capabilityMode,
    dataCollectionSourceState,
    marketplaceIntegration,
    configuration,
  } = usePrivateIntegrationsSyncContext()
  const { fetchIssues, issues, issuesLoading, anyValidSyncable, totalSyncables } =
    useSyncValidation()

  const closeModal = useCallback(() => setShowModal(false), [setShowModal])
  const issuesWithModalClose = useIssuesWithModalClose(issues, closeModal)

  const isExportMode = capabilityMode === 'file'
  const isExpenses = configuration.origin === 'expenses'
  const hasIssues = issues.length > 0
  const noData = !issuesLoading && totalSyncables === 0 && !hasIssues
  const integrationName = marketplaceIntegration?.displayName ?? ''

  const hasFilters = useMemo(() => {
    const { mappedVariablesJson } = dataCollectionSourceState
    if (!mappedVariablesJson) return false

    const mappedVariables: Record<string, unknown> = JSON.parse(mappedVariablesJson)
    return Object.entries(mappedVariables).some(
      ([key, value]) =>
        !EXCLUDED_DATA_COLLECTION_KEYS.includes(key) &&
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    )
  }, [dataCollectionSourceState])

  const handleSubmit = useCallback(async () => {
    setShowModal(false)
    onConfirm?.()
    await runSynchronisation()
  }, [onConfirm, runSynchronisation, setShowModal])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const result = await fetchIssues()
      if (!cancelled && skipConfirmationIfNoIssues && result && !result.hasIssues) {
        await handleSubmit()
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [fetchIssues, skipConfirmationIfNoIssues, handleSubmit])

  const getTitle = () => {
    if (issuesLoading) {
      return isExportMode
        ? i18n.t('integrations.sync_review_modal.validating_export_title')
        : i18n.t('integrations.sync_review_modal.validating_sync_title')
    }
    if (noData) {
      return isExportMode
        ? i18n.t('integrations.sync_review_modal.no_data_to_export_title')
        : i18n.t('integrations.sync_review_modal.no_data_to_sync_title')
    }
    if (hasIssues) {
      return isExportMode
        ? i18n.t('integrations.sync_review_modal.export_title')
        : i18n.t('integrations.sync_review_modal.sync_title')
    }
    return isExportMode
      ? i18n.t('integrations.sync_review_modal.ready_to_export_title')
      : i18n.t('integrations.sync_review_modal.ready_to_sync_title')
  }

  const getDescription = () => {
    if (noData) {
      return isExportMode
        ? i18n.t('integrations.sync_review_modal.no_data_to_export_description')
        : i18n.t('integrations.sync_review_modal.no_data_to_sync_description')
    }
    if (hasIssues) {
      return anyValidSyncable
        ? isExportMode
          ? i18n.t('integrations.sync_review_modal.export_warning_description')
          : i18n.t('integrations.sync_review_modal.sync_warning_description')
        : isExportMode
          ? i18n.t('integrations.sync_review_modal.export_description')
          : i18n.t('integrations.sync_review_modal.sync_description')
    }
    return isExportMode
      ? i18n.t('integrations.sync_review_modal.ready_to_export_description', {
          integration_name: integrationName,
        })
      : i18n.t('integrations.sync_review_modal.ready_to_sync_description', {
          integration_name: integrationName,
        })
  }

  const title = getTitle()
  const description = getDescription()

  const submitLabel = hasIssues
    ? isExportMode
      ? i18n.t('integrations.confirmation_modal.export_anyway_cta')
      : i18n.t('integrations.confirmation_modal.sync_anyway_cta')
    : isExportMode
      ? i18n.t('integrations.confirmation_modal.export_cta')
      : i18n.t('integrations.confirmation_modal.sync_cta')

  return (
    <SyncReviewModal
      onClose={() => setShowModal(false)}
      onSubmit={noData ? undefined : handleSubmit}
      issues={issuesWithModalClose}
      anyValidSyncable={anyValidSyncable}
      hasFilters={hasFilters}
      title={title}
      description={description}
      submitLabel={submitLabel}
      isExpenses={isExpenses}
      isLoading={issuesLoading}
    />
  )
}
